const TENANT_ID_PATTERN = /^[a-z0-9-]+$/;
const LOCALHOST_SUFFIX = ".localhost";
const PROD_DOMAIN_SUFFIX = ".edthos.app";

export const normalizeTenantId = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }
  return value.trim().toLowerCase();
};

export const isValidTenantId = (tenantId) => {
  return TENANT_ID_PATTERN.test(tenantId);
};

export const getCurrentHostname = () => {
  if (typeof window === "undefined" || !window.location) {
    return "";
  }
  return normalizeTenantId(window.location.hostname);
};

const extractSubdomain = (hostname, suffix) => {
  if (!hostname.endsWith(suffix)) {
    return "";
  }
  const candidate = hostname.slice(0, -suffix.length);
  if (!candidate || candidate.includes(".")) {
    return "";
  }
  return candidate;
};

const isPlainLocalDevelopmentHost = (normalizedHost) => {
  return (
    normalizedHost === "localhost" ||
    normalizedHost === "127.0.0.1" ||
    normalizedHost === "::1"
  );
};

export const resolveTenantIdFromHostname = (hostname) => {
  const normalizedHost = normalizeTenantId(hostname);
  if (!normalizedHost) {
    return "";
  }

  if (isPlainLocalDevelopmentHost(normalizedHost)) {
    const previewTenant = normalizeTenantId(process.env.REACT_APP_PREVIEW_TENANT_ID || "");
    if (isValidTenantId(previewTenant)) {
      return previewTenant;
    }
    return "";
  }

  const localhostSubdomain = extractSubdomain(normalizedHost, LOCALHOST_SUFFIX);
  if (localhostSubdomain && isValidTenantId(localhostSubdomain)) {
    return localhostSubdomain;
  }

  const productionSubdomain = extractSubdomain(normalizedHost, PROD_DOMAIN_SUFFIX);
  if (productionSubdomain && isValidTenantId(productionSubdomain)) {
    return productionSubdomain;
  }

  // Vercel preview hosts have no school subdomain; use the same preview tenant as local dev.
  if (normalizedHost.endsWith(".vercel.app")) {
    const previewTenant = normalizeTenantId(process.env.REACT_APP_PREVIEW_TENANT_ID || "");
    if (isValidTenantId(previewTenant)) {
      return previewTenant;
    }
  }

  return "";
};

export const getTenantContext = () => {
  const hostname = getCurrentHostname();
  const tenantId = resolveTenantIdFromHostname(hostname);
  return { hostname, tenantId };
};

export const parseTenantMap = (rawMap) => {
  if (!rawMap || typeof rawMap !== "string") {
    return {};
  }

  try {
    const parsed = JSON.parse(rawMap);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    const normalizedMap = {};
    Object.entries(parsed).forEach(([tenantId, config]) => {
      const normalizedTenantId = normalizeTenantId(tenantId);
      if (!isValidTenantId(normalizedTenantId)) {
        return;
      }
      if (!config || typeof config !== "object") {
        return;
      }

      const url = typeof config.url === "string" ? config.url.trim() : "";
      const anonKey = typeof config.anonKey === "string" ? config.anonKey.trim() : "";
      if (!url || !anonKey) {
        return;
      }

      normalizedMap[normalizedTenantId] = { url, anonKey };
    });

    return normalizedMap;
  } catch (error) {
    console.error("[tenantConfig] Invalid REACT_APP_TENANT_SUPABASE_MAP JSON:", error);
    return {};
  }
};

export const getEnvTenantMap = () => {
  return parseTenantMap(process.env.REACT_APP_TENANT_SUPABASE_MAP || "");
};

export const getLocalTenantConfig = (tenantId) => {
  const normalizedTenantId = normalizeTenantId(tenantId);
  if (!isValidTenantId(normalizedTenantId)) {
    return null;
  }

  const map = getEnvTenantMap();
  return map[normalizedTenantId] || null;
};

export const shouldUseRuntimeTenantLookup = () => {
  return process.env.REACT_APP_ENABLE_RUNTIME_TENANT_LOOKUP === "true";
};

export const getTenantConfigEndpoint = () => {
  return process.env.REACT_APP_TENANT_CONFIG_ENDPOINT || "/api/tenant-config";
};

